import { RegisterLifterCompetitionType } from './register-lifter-competition-type';
import { RegisterLifterPersonSearch } from './register-lifter-person-search';
import { makeRegisterLifterSchema, Schema, SchemaInput } from './register-lifter-form-schema';
import { RegisterLifterAgeCategory } from './register-lifter-age-category';
import { RegisterLifterWeightClass } from './register-lifter-weight-class';
import { CompetitionTeamSelect } from '~/pages/competition/teams/components/competition-team-select';
import { useUnsavedChangesGuard } from '~/hooks/useUnsavedChangesGuard';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '~/components/Button/Button';
import { Grid } from '~/components/Grid/Grid';
import { Input } from '~/components/Input';
import { useMemo } from 'react';

interface Props {
  onSubmit: (data: Schema) => void;
  isLoading?: boolean;
  mode?: 'create' | 'edit';
  defaultValues?: Partial<SchemaInput>;
  personName?: string;
  competitionId?: number;
  isTeamComp?: boolean;
}

export function RegisterLifterForm(props: Props) {
  const {
    isLoading = false,
    mode = 'create',
    defaultValues,
    personName,
    competitionId,
    isTeamComp = false,
    onSubmit: onSubmitProp,
  } = props;

  const resolver = useMemo(
    () => zodResolver(makeRegisterLifterSchema(isTeamComp)) as unknown as Resolver<Schema>,
    [isTeamComp]
  );

  const form = useForm<Schema>({
    resolver,
    mode: 'onChange',
    defaultValues: defaultValues as Partial<Schema>,
  });

  const { dialog } = useUnsavedChangesGuard(form.formState);

  function onSubmit(payload: Schema) {
    form.reset();
    onSubmitProp(payload);
  }

  // In team competitions a lifter is registered by team alone — the registration category
  // (tävlingstyp / ålderskategori / viktklass) does not apply. is_reserve switches the team
  // list between teams with normal-roster slots and teams with reserve slots, so it is picked
  // before the team.
  const isReserve = String(form.watch('is_reserve')) === 'true';

  const reserveSelect = isTeamComp && (
    <Input.Select
      {...form.register('is_reserve')}
      id="is_reserve"
      value={isReserve ? 'true' : 'false'}
      options={[
        ['Ordinarie', 'false'],
        ['Reserv', 'true'],
      ]}
      aria-label="Anmälningstyp"
      formState={form.formState}
    />
  );

  const teamSelect = isTeamComp && competitionId && (
    <CompetitionTeamSelect
      query={{
        is_reserve: isReserve,
        person: form.watch('person_id'),
        registerable: true,
      }}
      value={form.watch('team_id') ?? undefined}
      competitionId={competitionId}
      {...form.register('team_id')}
      formState={form.formState}
    />
  );

  // Edit mode - proper grid layout with labels
  if (mode === 'edit') {
    return (
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} autoComplete="off">
          <Grid>
            <Grid.Item cols={{ sm: 12, md: 6 }}>
              <Input.Group htmlFor="person_id" label="Lyftare">
                <Input.Field type="text" value={personName} disabled id="person" />
              </Input.Group>
            </Grid.Item>
            {isTeamComp ? (
              <>
                {reserveSelect && (
                  <Grid.Item cols={{ sm: 12, md: 6 }}>
                    <Input.Group formState={form.formState} htmlFor="is_reserve" label="Anmälningstyp">
                      {reserveSelect}
                    </Input.Group>
                  </Grid.Item>
                )}
                {teamSelect && (
                  <Grid.Item cols={{ sm: 12, md: 6 }}>
                    <Input.Group formState={form.formState} htmlFor="team_id" label="Lag">
                      {teamSelect}
                    </Input.Group>
                  </Grid.Item>
                )}
              </>
            ) : (
              <Grid.Item cols={{ sm: 12, md: 6 }}>
                <Input.Group formState={form.formState} htmlFor="competition_type_id" label="Tävlingstyp">
                  <RegisterLifterCompetitionType />
                </Input.Group>
              </Grid.Item>
            )}
          </Grid>

          {!isTeamComp && (
            <Grid>
              <Grid.Item cols={{ sm: 12, md: 6 }}>
                <Input.Group formState={form.formState} htmlFor="category_id" label="Ålderskategori">
                  <RegisterLifterAgeCategory />
                </Input.Group>
              </Grid.Item>
              <Grid.Item cols={{ sm: 12, md: 6 }}>
                <Input.Group formState={form.formState} htmlFor="weight_class_id" label="Viktklass">
                  <RegisterLifterWeightClass />
                </Input.Group>
              </Grid.Item>
            </Grid>
          )}

          <Button.Group align="right">
            <Button type="submit" isLoading={isLoading}>
              Spara
            </Button>
          </Button.Group>

          {dialog}
        </form>
      </FormProvider>
    );
  }

  // Create mode - inline layout for quick adding
  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} autoComplete="off">
        <Grid style={{ marginBottom: 'var(--size-6)' }}>
          <Grid.Item cols={{ sm: 12, md: 3 }}>
            <RegisterLifterPersonSearch />
          </Grid.Item>
          {isTeamComp ? (
            <>
              {reserveSelect && <Grid.Item cols={{ sm: 12, md: 3 }}>{reserveSelect}</Grid.Item>}
              {teamSelect && <Grid.Item cols={{ sm: 12, md: 3 }}>{teamSelect}</Grid.Item>}
              <Grid.Item cols={{ sm: 12, md: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <Button type="submit" style={{ flexShrink: 0 }} isLoading={isLoading}>
                    Lägg till
                  </Button>
                </div>
              </Grid.Item>
            </>
          ) : (
            <>
              <Grid.Item cols={{ sm: 6, md: 3 }}>
                <RegisterLifterCompetitionType />
              </Grid.Item>
              <Grid.Item cols={{ sm: 6, md: 3 }}>
                <RegisterLifterAgeCategory />
              </Grid.Item>
              <Grid.Item cols={{ sm: 6, md: 3 }}>
                <RegisterLifterWeightClass />
              </Grid.Item>
              <Grid.Item cols={{ sm: 6 }}>
                <Button type="submit" style={{ flexShrink: 0 }} isLoading={isLoading}>
                  Lägg till
                </Button>
              </Grid.Item>
            </>
          )}
        </Grid>

        {dialog}
      </form>
    </FormProvider>
  );
}
